import { useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ApiError, getCollection } from '../api/client'
import type { ProductSummary } from '../api/types'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { ProductGrid } from '../components/ProductGrid'
import { Fehlerhinweis, Leerhinweis, RasterSkelett } from '../components/States'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { useSprache, type Textschluessel } from '../i18n'
import { useShopConfig } from '../shopConfig'
import { NotFoundPage } from './NotFoundPage'

/**
 * Wie sortiert werden kann.
 *
 * Nur hier und nicht in der Suche: Eine Sammlung kommt vollständig vom Server,
 * die Suche seitenweise. Im Browser sortiert hieße dort, nur die aktuelle Seite
 * zu ordnen — der günstigste Artikel stünde oben, obwohl auf Seite drei ein
 * günstigerer liegt.
 */
const SORTIERUNGEN: { wert: string; name: Textschluessel; ordne?: (a: ProductSummary, b: ProductSummary) => number }[] = [
  // Die Reihenfolge des Händlers: Er hat seine Sammlung so geordnet, wie er
  // sie zeigen will, und das ist die Vorgabe.
  { wert: 'empfohlen', name: 'sammlung.sortEmpfohlen' },
  { wert: 'preis-auf', name: 'sammlung.sortPreisAuf', ordne: (a, b) => a.priceFrom - b.priceFrom },
  { wert: 'preis-ab', name: 'sammlung.sortPreisAb', ordne: (a, b) => b.priceFrom - a.priceFrom },
  { wert: 'name', name: 'sammlung.sortName', ordne: (a, b) => a.name.localeCompare(b.name) },
]

export function CollectionPage() {
  const { t, tz } = useSprache()
  const { handle = '' } = useParams()
  const { shopName, primaryHost } = useShopConfig()
  const { data, error, loading } = useResource((signal) => getCollection(handle, signal), [handle])

  // Die Sortierung steht in der Adresse und nicht in einem Zustand: Wer ein
  // Produkt öffnet und zurückgeht, soll dieselbe Reihenfolge wiederfinden, und
  // eine geteilte Adresse zeigt dem Empfänger dasselbe.
  const [frage, setFrage] = useSearchParams()
  const sortierung = SORTIERUNGEN.find((s) => s.wert === frage.get('sort')) ?? SORTIERUNGEN[0]!

  const produkte = useMemo(() => {
    const liste = data?.products ?? []
    // Ausverkauftes ans Ende, bei jeder Sortierung: Wer nach dem Preis
    // sortiert, will oben etwas sehen, das er kaufen kann.
    const geordnet = sortierung.ordne ? [...liste].sort(sortierung.ordne) : [...liste]
    return geordnet.sort((a, b) => Number(b.available) - Number(a.available))
  }, [data, sortierung])

  const sammlung = data?.collection
  useSeo({
    title: sammlung
      ? t('sammlung.seoTitel', { name: sammlung.name, laden: shopName })
      : t('sammlung.seoTitelAlle', { laden: shopName }),
    description: sammlung?.description || t('sammlung.seoText', { name: sammlung?.name ?? '', laden: shopName }),
    canonicalHost: primaryHost,
  })

  if (error instanceof ApiError && error.isNotFound) {
    return <NotFoundPage was={t('sammlung.nichtGefunden')} />
  }

  return (
    <>
      <Breadcrumbs
        pfade={[
          { name: t('nav.startseite'), pfad: '/' },
          { name: t('nav.sammlungen'), pfad: '/sammlung' },
          { name: sammlung?.name ?? t('sammlung.eine') },
        ]}
      />

      <header className="sf-seitenkopf" data-mit-bild={sammlung?.imageUrl ? 'ja' : 'nein'}>
        <div>
          <h1>{sammlung?.name ?? t('sammlung.eine')}</h1>
          {sammlung?.description && <p>{sammlung.description}</p>}
        </div>
        {sammlung?.imageUrl && (
          <div className="sf-seitenkopf-bild">
            <img src={sammlung.imageUrl} alt="" decoding="async" />
          </div>
        )}
      </header>

      {loading && <RasterSkelett />}
      {error != null && <Fehlerhinweis fehler={error} />}
      {data?.products.length === 0 && <Leerhinweis text={t('sammlung.leer')} />}

      {produkte.length > 0 && (
        <>
          <div className="sf-leiste">
            <span>{tz(produkte.length, 'sammlung.produktEins', 'sammlung.produktViele')}</span>
            <label className="sf-auswahl">
              <span>{t('sammlung.sortieren')}</span>
              <select
                value={sortierung.wert}
                onChange={(ereignis) => {
                  const neu = new URLSearchParams(frage)
                  if (ereignis.target.value === 'empfohlen') neu.delete('sort')
                  else neu.set('sort', ereignis.target.value)
                  // replace: Jede Sortierung als eigener Verlaufsschritt hieße,
                  // dreimal „Zurück" zu drücken, um die Sammlung zu verlassen.
                  setFrage(neu, { replace: true })
                }}
              >
                {SORTIERUNGEN.map((s) => (
                  <option key={s.wert} value={s.wert}>
                    {t(s.name)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <ProductGrid produkte={produkte} />
        </>
      )}
    </>
  )
}
